import React, { useEffect, useState } from "react";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  FileOutlined,
  FileImageOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import {
  Modal,
  Button,
  Tag,
  Descriptions,
  Input,
  Space,
  message,
  Table,
  Card,
  Image,
  Typography,
  Col,
  Row,
} from "antd";
import { filterUsers } from "../../api/UserAPI";
import { useGlobalUI } from "../../context/GlobalUIProvider";
import { getFilePath } from "../../config/app-config";
import { updateKyc } from "../../api/WalletApi";
import { kycSummary } from "../../api/KycAPI";
import {
  decryptData,
  formatIdWithPrefix,
  maskString,
} from "../../Custom/Custom";
import FindUserModal from "../UserManagmentPackage/FindUserModal";
import { SearchIcon } from "lucide-react";

const submissions = [
  {
    id: "KYC001",
    user: "John Doe",
    email: "john@email.com",
    docType: "Passport",
    date: "2024-01-20",
    priority: "High",
    status: "Pending",
  },
  // ... other items
];

function priorityTag(priority) {
  const color =
    priority === "High" ? "red" : priority === "Medium" ? "orange" : "blue";
  return <Tag color={color}>{priority}</Tag>;
}

function statusTag(status) {
  const color =
    status === "APPROVED"
      ? "green"
      : status === "REJECTED"
      ? "red"
      : status === "UNDER REVIEW"
      ? "orange"
      : "blue";
  return <Tag color={color}>{status}</Tag>;
}

export default function KycManagement() {
  const { Title, Text } = Typography;

  const { message, showLoader, hideLoader } = useGlobalUI();
  const [users, setUsers] = useState([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isFindUserModalOpen, setIsFindUserModalOpen] = useState(false);
  const [isMasked, setIsMasked] = useState(true);
  const [kycCount, setKycCount] = useState(null);
  const [complianceNotes, setComplianceNotes] = useState("");
  const [pagination, setPagination] = useState({
    totalItems: 0,
    currentPage: 0,
    pageSize: 10,
  });

  const fetchUsers = (page = 0, size = 10, filterData = {}) => {
    showLoader();
    filterUsers({ pageNo: page, size, ...filterData })
      .then((res) => {
        if (res.data && res.data.data) {
          const { users, totalItems, currentPage } = res.data.data;

          // Map nested API data into a flat table row structure
          const mappedUsers = (users || []).map((u) => {
            // Call this separately
            // setComplianceNotes(u.kyc?.notes);

            // Return the mapped object
            return {
              userId: u.id,
              id: u.kyc?.id,
              user: { name: u.name, email: u.email },
              docType: u.kyc?.documentType || "-",
              date: u.kyc?.createdDate
                ? new Date(u.kyc.createdDate).toLocaleDateString()
                : "-",
              priority: u.kyc?.priority || "Medium",
              status: u.kyc?.status || "Pending",
              addressProofFile: u.kyc?.addressProofFile,
              idProofFile: u.kyc?.idProofFile,
              otherProofFile: u.kyc?.otherProofFile,
              notes: u.kyc?.notes || "",
            };
          });

          setUsers(mappedUsers);
          setPagination({
            totalItems: totalItems || 0,
            currentPage: currentPage || 0,
            pageSize: size,
          });
        } else {
          setUsers([]);
        }
      })
      .catch(() => message.error("Failed to fetch users"))
      .finally(() => hideLoader());
  };

  useEffect(() => {
    fetchUsers();
    getAllKycSummary();
  }, []);

  const getAllKycSummary = () => {
    showLoader();
    kycSummary()
      .then((response) => {
        if (response.data.data) {
          setKycCount(response.data.data);
        }
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => hideLoader());
  };

  const handleFindUser = (filtersData) => {
    setIsFindUserModalOpen(false);
    fetchUsers(0, pagination.pageSize, filtersData);
    setIsMasked(false);
  };

  const stats = [
    {
      label: "Pending Review",
      value: kycCount?.kycPendingReview,
      color: "processing",
    },
    {
      label: "Approved Today",
      value: kycCount?.kycApprovedToday,
      color: "success",
    },
    {
      label: "Rejected Today",
      value: kycCount?.kycRejectedToday,
      color: "error",
    },
    {
      label: "Total Submissions",
      value: kycCount?.totalKycSubmission,
      color: "default",
    },
  ];

  console.log("complianceNotes", complianceNotes);

  const openReviewModal = (submission) => {
    console.log("submission", submission);
    setSelectedSubmission(submission);
    setComplianceNotes(submission.notes);
    setReviewModalOpen(true);
  };

  const approveSubmission = () => {
    let obj = {
      userId: selectedSubmission.userId,
      status: 2,
      notes: complianceNotes,
    };
    showLoader();
    updateKyc(obj)
      .then((response) => {
        if (response.data.status === 1) {
          message.success(`Submission ${selectedSubmission.userId} approved`);
          fetchUsers();
          setIsMasked(true);
          getAllKycSummary();
          setReviewModalOpen(false);
        } else {
          message.error("Error Occured");
        }
      })
      .finally(() => hideLoader());
  };

  const rejectSubmission = () => {
    setReviewModalOpen(false);

    let obj = {
      userId: selectedSubmission.userId,
      status: 3,
      notes: complianceNotes,
    };
    showLoader();
    updateKyc(obj)
      .then((response) => {
        if (response.data.status === 1) {
          message.success(`Submission ${selectedSubmission.userId} rejected`);
          fetchUsers();
          setIsMasked(true);
          getAllKycSummary();
          setReviewModalOpen(false);
        } else {
          message.error("Error Occured");
        }
      })
      .finally(() => hideLoader());
  };

  // Table column config using antd Table
  let columns = [
    // {
    //   title: "Submission ID",
    //   dataIndex: "id",
    //   key: "id",
    //   render: (text) => <Text strong>{formatIdWithPrefix(text, "KYC")}</Text>,
    // },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user) => (
        <>
          <div>{isMasked ? maskString(user.name, 1, 0) : user.name}</div>
          <div style={{ color: "#888" }}>
            {isMasked ? maskString(user.email, 2, 2) : user.email}
          </div>
        </>
      ),
    },
    { title: "Submission Date", dataIndex: "date", key: "date" },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: priorityTag,
      filters: [
        { text: "High", value: "High" },
        { text: "Medium", value: "Medium" },
        { text: "Low", value: "Low" },
      ],
      onFilter: (value, record) => record.priority === value,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: statusTag,
      filters: [
        { text: "PENDING", value: "Pending" },
        { text: "UNDER REVIEW", value: "Under Review" },
        { text: "APPROVED", value: "Approved" },
        { text: "REJECTED", value: "Rejected" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   render: (_, record) => (
    //     <Space>
    //       <Button
    //         icon={<EyeOutlined />}
    //         onClick={() => openReviewModal(record)}
    //         size="small"
    //       >
    //         Review
    //       </Button>
    //       {record.status === "Pending" && (
    //         <>
    //           <Button
    //             icon={<CheckOutlined />}
    //             type="primary"
    //             size="small"
    //             onClick={() => {
    //               setSelectedSubmission(record);
    //               approveSubmission();
    //             }}
    //           />
    //           <Button
    //             icon={<CloseOutlined />}
    //             danger
    //             size="small"
    //             onClick={() => {
    //               setSelectedSubmission(record);
    //               rejectSubmission();
    //             }}
    //           />
    //         </>
    //       )}
    //     </Space>
    //   ),
    // },
  ];

  if (!isMasked) {
    columns.push({
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => openReviewModal(record)}
            size="small"
          >
            Review
          </Button>
          {record.status === "Pending" && (
            <>
              <Button
                icon={<CheckOutlined />}
                type="primary"
                size="small"
                onClick={() => {
                  setSelectedSubmission(record);
                  approveSubmission();
                }}
              />
              <Button
                icon={<CloseOutlined />}
                danger
                size="small"
                onClick={() => {
                  setSelectedSubmission(record);
                  rejectSubmission();
                }}
              />
            </>
          )}
        </Space>
      ),
    });
  }

  // Updated renderUpload to include title & column layout
  const renderUpload = (label, fileState) => (
    <Col span={8} style={{ textAlign: "center" }}>
      <div style={{ fontWeight: 500, marginBottom: 8 }}>{label}</div>
      {fileState ? (
        /\.(jpg|jpeg|png|gif)$/i.test(fileState) ? (
          <Image
            preview={{ mask: <span>Click to preview</span> }}
            src={getFilePath(fileState)}
            alt={label}
            width={50}
            style={{ cursor: "pointer", borderRadius: 4 }}
            placeholder={
              <FileImageOutlined style={{ fontSize: 40, color: "#999" }} />
            }
          />
        ) : (
          <a
            href={getFilePath(fileState)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <FileOutlined style={{ fontSize: 40 }} />
            View File
          </a>
        )
      ) : (
        <span style={{ color: "#999" }}>No file uploaded</span>
      )}
    </Col>
  );

  const clearFilter = () => {
    setIsMasked(true);
    fetchUsers();
  };

  return (
    <div className="kyc-root">
      {/* <div className="kyc-header-row">
        <div>
          <h1 className="kyc-title">KYC Management</h1>
          <span className="kyc-desc">
            Review and manage user verification documents
          </span>
        </div>
      </div> */}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        {/* Left Section */}
        <Col>
          <div style={{ fontSize: 28, fontWeight: 600 }}>KYC Management</div>
          <div style={{ color: "#555" }}>
            Review and manage user verification documents
          </div>
        </Col>

        {/* Right Section */}
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<ClearOutlined />}
              onClick={() => clearFilter()}
            >
              Clear
            </Button>
            <Button
              type="primary"
              icon={<SearchIcon />}
              onClick={() => setIsFindUserModalOpen(true)}
            >
              Find User
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Stats */}
      <div className="kyc-stats-grid">
        {stats.map((c) => (
          <div className={`kyc-stat-card ${c.color}`} key={c.label}>
            <div className={`kyc-stat-value ${c.color}`}>{c.value}</div>
            <span className="kyc-stat-label">{c.label}</span>
          </div>
        ))}
      </div>

      <Card title="Recent KYC Submissions">
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          pagination={{
            current: pagination.currentPage + 1,
            pageSize: pagination.pageSize,
            total: pagination.totalItems,
            onChange: (page) => fetchUsers(page - 1, pagination.pageSize),
          }}
        />
      </Card>

      <FindUserModal
        open={isFindUserModalOpen}
        onCancel={() => setIsFindUserModalOpen(false)}
        onFind={handleFindUser}
      />

      {/* Modal Review */}
      <Modal
        title={`Review Submission - ${"KYC" + selectedSubmission?.id || ""}`}
        open={reviewModalOpen}
        onCancel={() => setReviewModalOpen(false)}
        centered
        width={650}
        footer={[
          <Button key="cancel" onClick={() => setReviewModalOpen(false)}>
            Close
          </Button>,
          (selectedSubmission?.status !== "REJECTED" ||
            selectedSubmission?.status === "PENDING") && (
            <Button
              key="reject"
              danger
              icon={<CloseOutlined />}
              onClick={rejectSubmission}
            >
              Reject
            </Button>
          ),
          (selectedSubmission?.status !== "APPROVED" ||
            selectedSubmission?.status === "PENDING") && (
            <Button
              key="approve"
              type="primary"
              icon={<CheckOutlined />}
              onClick={approveSubmission}
            >
              Approve
            </Button>
          ),
        ]}
      >
        {selectedSubmission && (
          <>
            <Descriptions
              bordered
              size="small"
              column={1}
              style={{ marginBottom: "1rem" }}
            >
              <Descriptions.Item label="User">
                {selectedSubmission.user.name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedSubmission.user.email}
              </Descriptions.Item>

              <Descriptions.Item label="Submission Date">
                {selectedSubmission.date}
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                {priorityTag(selectedSubmission.priority)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {statusTag(selectedSubmission.status)}
              </Descriptions.Item>
            </Descriptions>

            {/* File preview section */}

            <Row gutter={[16, 16]} style={{ marginBottom: "1rem" }}>
              {renderUpload(
                "Address Proof",
                selectedSubmission.addressProofFile
              )}
              {renderUpload("Id Proof", selectedSubmission.idProofFile)}
              {renderUpload("Other Proof", selectedSubmission.otherProofFile)}
            </Row>

            <div style={{ marginTop: "1rem" }}>
              <label
                style={{ display: "block", fontWeight: 500, marginBottom: 4 }}
              >
                Compliance Notes / Workflow
              </label>
              <Input.TextArea
                rows={4}
                placeholder="Enter notes, required actions, or workflow updates..."
                value={complianceNotes}
                onChange={(e) => setComplianceNotes(e.target.value)}
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
